import clsx from 'clsx'
import type { PowerPerformanceForms } from '../../../../types/mutual-fund-detail.types'
import { mfLogoBaseUrl } from '../../../../env'
import { toPascalCaseWithSpaces } from '../../../../utils/utils'
import { LeafRank } from '../../../../components/know-why/LeafRank'
import { PowerFormBadge } from '../../../../components/ui-components/PowerFormBadge'
import { RegularTag } from '../../../../components/ui-components/RegularTag'

const BORDER_DEFAULT = 'border-[#F0F4F7]'

export enum FundCardBorderVariant {
  FULL = 'full',
  BOTTOM_FULL = 'bottom-full',
  BOTTOM_HALF = 'bottom-half',
  NO_BORDER = 'no-border'
}

/**
 * Three-column layout: fundLogo | content (fundTitle, fundSubtitle, optional badge row, optional helper rows) | trailingContent.
 * trailingContentAlign: 'center' = vertically centered in the row (e.g. chevron); 'start' = top (e.g. amount).
 * Logo, title and "nature / category" subtitle are rendered inside. helperRows render below BadgeRow with overflow-visible.
 * borderVariant: FULL = border all around; BOTTOM_FULL = only bottom border full width; BOTTOM_HALF = only bottom border under content. Default FULL.
 * className applies to the card wrapper so padding and other styles can be controlled.
 */
type FundCardLayoutProps = {
  fundLogo: string
  fundTitle: string
  fundNature?: string
  fundCategory?: string
  trailingContent?: React.ReactNode
  trailingContentAlign?: 'center' | 'start'
  powerRank?: number
  powerRating?: `${PowerPerformanceForms}`
  isRegular?: boolean
  helperRows?: React.ReactNode[]
  onClick?: () => void
  className?: string
  borderVariant?: FundCardBorderVariant
  borderClassName?: string
  topPadding?: number
  rightPadding?: number
  bottomPadding?: number
  leftPadding?: number
}

const LOGO_SIZE = 'w-10 h-10'

export function FundCardLayout({
  fundLogo,
  fundTitle,
  fundNature,
  fundCategory,
  trailingContent,
  trailingContentAlign = 'center',
  powerRank,
  powerRating,
  isRegular,
  helperRows,
  onClick,
  className = '',
  borderVariant = FundCardBorderVariant.FULL,
  borderClassName = BORDER_DEFAULT,
  topPadding = 16,
  rightPadding = 16,
  bottomPadding = 15,
  leftPadding = 16,
}: FundCardLayoutProps) {
  const showBadgeRow = powerRank != null || powerRating != null || isRegular
  const hasSubtitle = (fundNature?.length ?? 0) > 0 || (fundCategory?.length ?? 0) > 0
  const subtitleText =
    hasSubtitle &&
    [fundNature ? toPascalCaseWithSpaces(fundNature) : '', fundCategory ? toPascalCaseWithSpaces(fundCategory) : '']
      .filter(Boolean)
      .join(' / ')

  const isFullBorder = borderVariant === FundCardBorderVariant.FULL
  const isBottomFull = borderVariant === FundCardBorderVariant.BOTTOM_FULL
  const isBottomHalf = borderVariant === FundCardBorderVariant.BOTTOM_HALF
  const isNoBorder = borderVariant === FundCardBorderVariant.NO_BORDER

  const cardClasses = clsx(
    'flex flex-col w-full bg-white overflow-visible',
    onClick != null && 'cursor-pointer',
    isFullBorder && `border rounded-xl ${borderClassName}`,
    isBottomFull && `border-b ${borderClassName}`,
    isNoBorder && 'border-none',
    className
  )

  const mainContentWrapperClasses = clsx(
    'flex gap-2 min-w-0 flex-1',
    isBottomHalf && `border-b ${borderClassName}`,
    isNoBorder && 'border-none'
  )

  return (
    <div className={cardClasses} onClick={onClick}>
      <div
        style={{
          paddingTop: topPadding,
          paddingLeft: leftPadding
        }}
        className="flex gap-4 items-stretch"
      >
        <div className={`${LOGO_SIZE} flex-shrink-0`}>
          <img
            className="h-full w-full object-contain"
            src={getLogoUrl(fundLogo)}
            alt=""
          />
        </div>
        <div style={{ paddingBottom: bottomPadding }} className={mainContentWrapperClasses}>
          <div className="flex flex-col min-w-0 flex-1 overflow-visible">
            <div className="text-[13px] text-black leading-[140%] max-w-[210px] 
            font-[400] line-clamp-2" title={fundTitle ?? undefined}>
              {fundTitle}
            </div>
            {subtitleText && (
              <div className="pt-[2px] text-[13px] text-[#6A6E6E]">{subtitleText}</div>
            )}
            {showBadgeRow && (
              <BadgeRow powerRank={powerRank} powerRating={powerRating} isRegular={isRegular} />
            )}
            {helperRows?.map((row, i) => (
              <div key={i} className="overflow-visible pt-3 w-max">
                {row}
              </div>
            ))}
          </div>
          {trailingContent != null && (
            <div
              style={{ paddingRight: rightPadding }}
              className={`flex-shrink-0 flex 
                ${trailingContentAlign === 'center' ? 'items-center' : 'items-start'}`}
            >
              {trailingContent}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export function BadgeRow({
  powerRank,
  powerRating,
  isRegular
}: {
  powerRank?: number
  powerRating?: `${PowerPerformanceForms}`
  isRegular?: boolean
}) {
  const hasRank = powerRank != null
  const hasRating = powerRating != null
  const hasRegular = !!isRegular
  if (!hasRank && !hasRating && !hasRegular) return null
  return (
    <div className="flex items-center gap-2 pt-4 overflow-visible w-max">
      {hasRank && (
        <LeafRank isElite powerRank={powerRank} powerForm={powerRating ?? null} />
      )}
      {hasRating && <PowerFormBadge powerForm={powerRating} />}
      {hasRegular && <RegularTag />}
    </div>
  )
}

export function getLogoUrl(logo?: string): string {
  return `${mfLogoBaseUrl}${logo}`
}
